import React from 'react';
import { 
  PencilSquareIcon, 
  TrashIcon, 
  EyeIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { Order, OrderStatus } from '../../types/order';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/formatters';
import { Tooltip } from '../common/Tooltip';
import DocumentChain from '../common/DocumentChain';
import OrderToDeliveryNoteButton from './OrderToDeliveryNoteButton';

interface OrderListProps {
  orders: Order[];
  onEditClick: (order: Order) => void;
  onDeleteClick: (order: Order) => void;
  onViewClick: (order: Order) => void;
  onStatusChange: (orderId: string, status: OrderStatus) => void;
  loading?: boolean;
}

const OrderList: React.FC<OrderListProps> = ({
  orders,
  onEditClick,
  onDeleteClick,
  onViewClick,
  onStatusChange,
  loading = false
}) => {
  const { t } = useLanguage();

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'production': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'ready': return 'bg-green-100 text-green-800 border-green-200';
      case 'shipped': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'delivered': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusBorderColor = (status: OrderStatus) => {
    switch (status) {
      case 'draft': return '#94a3b8';
      case 'pending': return '#eab308';
      case 'confirmed': return '#2563eb';
      case 'production': return '#7c3aed';
      case 'ready': return '#16a34a';
      case 'shipped': return '#4f46e5';
      case 'delivered': return '#059669';
      case 'cancelled': return '#dc2626';
      default: return '#94a3b8';
    }
  };

  const getStatusLabel = (status: OrderStatus) => {
    switch (status) {
      case 'draft': return 'Vázlat';
      case 'pending': return 'Függőben';
      case 'confirmed': return 'Megerősített';
      case 'production': return 'Gyártás alatt';
      case 'ready': return 'Kész';
      case 'shipped': return 'Kiszállítva';
      case 'delivered': return 'Leszállítva';
      case 'cancelled': return 'Törölve';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-12 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
        <div className="text-gray-500 dark:text-gray-400 text-lg mb-2">Nincs megrendelés</div>
        <div className="text-gray-400 dark:text-gray-500 text-sm">Itt jelennek meg a megrendelések</div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop táblázat nézet */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Megrendelés szám
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Ügyfél
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Dátum
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  Státusz
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-300">
                  Összeg
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  Szállítás
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-20">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {orders.map((order) => (
                <>
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 cursor-pointer"
                    style={{ borderLeftColor: getStatusBorderColor(order.status) }}
                    onClick={() => onViewClick(order)}
                  >
                    <td className="px-3 py-3">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {order.number}
                        </div>
                        {order.quotationId && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Árajánlat alapján
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {order.customerName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {order.customerEmail}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {new Date(order.orderDate).toLocaleDateString('hu-HU')}
                        </div>
                        {order.requestedDeliveryDate && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Kért: {new Date(order.requestedDeliveryDate).toLocaleDateString('hu-HU')}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={order.status}
                        onChange={(e) => onStatusChange(order.id, e.target.value as OrderStatus)}
                        className={`text-xs font-medium border rounded-full px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 ${getStatusColor(order.status)}`}
                      >
                        <option value="draft">Vázlat</option>
                        <option value="pending">Függőben</option>
                        <option value="confirmed">Megerősített</option>
                        <option value="production">Gyártás alatt</option>
                        <option value="ready">Kész</option>
                        <option value="shipped">Kiszállítva</option>
                        <option value="delivered">Leszállítva</option>
                        <option value="cancelled">Törölve</option>
                      </select>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(order.totalAmount, order.currency)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          nettó: {formatCurrency(order.subtotal, order.currency)}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <OrderToDeliveryNoteButton
                        order={order}
                        onDeliveryNoteCreated={(deliveryNoteId) => {
                          console.log('Szállítólevél létrehozva:', deliveryNoteId);
                        }}
                        className="text-blue-500 hover:text-blue-600"
                      />
                    </td>
                    <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center space-x-1">
                        <Tooltip content="Megtekintés">
                          <button
                            onClick={(e) => { e.stopPropagation(); onViewClick(order); }}
                            className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Szerkesztés">
                          <button
                            onClick={(e) => { e.stopPropagation(); onEditClick(order); }}
                            className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Törlés">
                          <button
                            onClick={(e) => { e.stopPropagation(); onDeleteClick(order); }}
                            className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                  {/* Bizonylat lánc sor */}
                  <tr key={`${order.id}-chain`} className="bg-gray-50 dark:bg-gray-700/50">
                    <td colSpan={7} className="px-3 py-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Bizonylat lánc:
                        </span>
                        <DocumentChain
                          documentId={order.id}
                          documentType="order"
                          className="text-xs"
                          compact={true}
                        />
                      </div>
                    </td>
                  </tr>
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobil kártya nézet */}
      <div className="lg:hidden space-y-4">
        {orders.map((order) => (
          <div
            key={order.id}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700 border-l-4 cursor-pointer"
            style={{ borderLeftColor: getStatusBorderColor(order.status) }}
            onClick={() => onViewClick(order)}
          >
            {/* Fejléc */}
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {order.number}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(order.orderDate).toLocaleDateString('hu-HU')}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-xs font-medium rounded-full px-2 py-1 border ${getStatusColor(order.status)}`}>
                  {getStatusLabel(order.status)}
                </span>
              </div>
            </div>

            {/* Ügyfél info */}
            <div className="mb-3">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {order.customerName}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {order.customerEmail}
              </div>
            </div>

            {/* Összeg */}
            <div className="mb-3">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(order.totalAmount, order.currency)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                nettó: {formatCurrency(order.subtotal, order.currency)}
              </div>
            </div>

            {/* Szállítás */}
            {order.requestedDeliveryDate && (
              <div className="mb-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Kért szállítás: {new Date(order.requestedDeliveryDate).toLocaleDateString('hu-HU')}
                </div>
              </div>
            )}

            {/* Bizonylat lánc */}
            <div className="mb-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Bizonylat lánc:
              </div>
              <DocumentChain
                documentId={order.id}
                documentType="order"
                className="text-xs"
                compact={true}
              />
            </div>

            {/* Műveletek */}
            <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-600">
              <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                <OrderToDeliveryNoteButton
                  order={order}
                  onDeliveryNoteCreated={(deliveryNoteId) => {
                    console.log('Szállítólevél létrehozva:', deliveryNoteId);
                  }}
                  className="text-blue-500 hover:text-blue-600"
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={(e) => { e.stopPropagation(); onViewClick(order); }}
                  className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onEditClick(order); }}
                  className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteClick(order); }}
                  className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default OrderList; 